import { useState } from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Select } from '@factorialco/f0-react/dist/experimental'
import { TrainingsSessionFrequencyEnum } from 'generated/resources/graphql'
import moment from 'moment'

import DayPicker from 'modules/core/components/DayPicker'
import TextField from 'modules/core/components/TextField'
import { dateBackendDateTimeToUI, stringToDate } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import { sumMinutesToHours } from 'modules/trainings/components/MyTrainings/RequestsComponent/RequestDetailComponent/useHoursAndMinutes'
import NumberField from 'modules/trainings/components/NumberField'
import {
  SessionData,
  calculateDuration,
} from 'modules/trainings/components/Revamp/NewClassWizard/hooks/useNewClassWizardSteps'
import { useSessionsForm } from 'modules/trainings/hooks/Sessions/useSessionsForm'
import { useModalities } from 'modules/trainings/hooks/useModalities'

import { EmployeeSelectorWithIds } from './EmployeeSelectorWithIds'

interface ScheduledEventBoxProps {
  data: SessionData
  setCurrentData: (value: SessionData) => void
  groupEnd: string
  groupStart: string
  featureFlag?: boolean
}

const UNDEFINED_VALUE = '__UNDEFINED__'

// edits the 'startsAt', 'endsAt' and 'duration' fields of the session.
export const ScheduledEvent = ({
  data,
  setCurrentData,
  groupEnd,
  groupStart,
}: ScheduledEventBoxProps) => {
  const { buildTime, setHourFromBackend } = useSessionsForm()
  const { modalityOptions } = useModalities()

  // define state for current form values
  const [startDate, setStartDate] = useState((): string => {
    // SessionData.startsAt is datetime in backend format '2025-04-30T07:00:00Z'
    // startDate is UI format '30/04/2025'
    return data.startsAt ? dateBackendDateTimeToUI(String(data.startsAt)) : ''
  })
  const [startsAtHour, setStartsAtHour] = useState((): string => {
    return data.startsAt ? setHourFromBackend(String(data.startsAt)) : ''
  })
  const [endsAtHour, setEndsAtHour] = useState((): string => {
    return data.startsAt ? setHourFromBackend(String(data.endsAt)) : ''
  })
  const [hours, setHours] = useState((): number => {
    return Math.floor(parseFloat(data.duration || '0'))
  })
  const [minutes, setMinutes] = useState((): number => {
    return Math.round((parseFloat(data.duration || '0') % 1) * 60)
  })

  function frequencyOptions(
    day?: string
  ): { value: TrainingsSessionFrequencyEnum | typeof UNDEFINED_VALUE; label: string }[] {
    const dayName = moment(day, 'L').format('dddd')

    const options: {
      value: TrainingsSessionFrequencyEnum | typeof UNDEFINED_VALUE
      label: string
    }[] = [
      {
        value: UNDEFINED_VALUE,
        label: i18n.t(
          'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.frequency.default'
        ),
      },
    ]

    if (!day) return options

    const frequencies: {
      value: TrainingsSessionFrequencyEnum | typeof UNDEFINED_VALUE
      label: string
    }[] = [
      {
        label: i18n.t(
          'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.frequency.weekly',
          { dayName: dayName }
        ),
        value: 'weekly',
      },
      {
        label: i18n.t(
          'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.frequency.daily'
        ),
        value: 'daily',
      },
      {
        label: i18n.t(
          'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.frequency.monthly'
        ),
        value: 'monthly',
      },
    ]

    return [...options, ...frequencies]
  }

  // when 'Date' is changed, calculate 'startsAt' and 'endsAt'
  const handleDateChange = (date: string) => {
    setStartDate(date)
    setCurrentData({
      ...data,
      startsAt: startsAtHour ? buildTime(date, startsAtHour) : undefined, //save only if we have starts at hour
      endsAt: endsAtHour ? buildTime(date, endsAtHour) : undefined, //save only if we have ends at hour
    })
  }

  // When 'Starts at' or 'Ends at' are changed, calculate changed field and duration
  const handleTimeChange = (timeType: 'startsAtHour' | 'endsAtHour', timeValue: string) => {
    const isStart = timeType === 'startsAtHour'
    const otherTime = isStart ? endsAtHour : startsAtHour

    if (otherTime && timeValue) {
      const calculatedDuration = isStart
        ? calculateDuration(timeValue, otherTime) // start time changed
        : calculateDuration(otherTime, timeValue) // end time changed

      //save new duration and new date & hour field
      setCurrentData({
        ...data,
        duration: calculatedDuration.duration,
        [isStart ? 'startsAt' : 'endsAt']: buildTime(startDate ?? '', timeValue),
      })
      isStart ? setStartsAtHour(timeValue) : setEndsAtHour(timeValue)
      setHours(calculatedDuration.hours)
      setMinutes(calculatedDuration.minutes)
    } else {
      //save new date & hour field
      setCurrentData({
        ...data,
        [isStart ? 'startsAt' : 'endsAt']: buildTime(startDate ?? '', timeValue),
      })
      isStart ? setStartsAtHour(timeValue) : setEndsAtHour(timeValue)
    }
  }

  // When 'hours' or 'minutes' are changed, calculate 'startsAt', 'endsAt' and 'duration'
  const handleDurationChange = (newHours: number, newMinutes: number) => {
    // NumberField does not handle readonly parameter
    if (!startsAtHour && !endsAtHour) {
      return
    }

    //by default, we use the current hours
    let newStartsAtHour = startsAtHour,
      newEndsAtHour = endsAtHour
    if (startsAtHour && !endsAtHour) {
      //we have start hour only, calculate end hour
      newEndsAtHour = moment(startsAtHour, 'HH:mm')
        .add(newHours, 'hours')
        .add(newMinutes, 'minutes')
        .format('HH:mm')
      setCurrentData({
        ...data,
        duration: String(sumMinutesToHours(Number(newHours), Number(newMinutes))),
        endsAt: buildTime(startDate, newEndsAtHour),
      })
      setEndsAtHour(newEndsAtHour)
    } else if (endsAtHour && !startsAtHour) {
      //we have end hour only, calculate start hour
      newStartsAtHour = moment(endsAtHour, 'HH:mm')
        .subtract(newHours, 'hours')
        .subtract(newMinutes, 'minutes')
        .format('HH:mm')
      setCurrentData({
        ...data,
        duration: String(sumMinutesToHours(Number(newHours), Number(newMinutes))),
        startsAt: buildTime(startDate, newStartsAtHour),
      })
      setStartsAtHour(newStartsAtHour)
    } else {
      //don't change start and end, only duration
      setCurrentData({
        ...data,
        duration: String(sumMinutesToHours(Number(newHours), Number(newMinutes))),
      })
    }

    setHours(newHours)
    setMinutes(newMinutes)
  }

  return (
    <>
      <Box flexDirection="row" justifyContent="spaceBetween" gap="s16">
        <DayPicker
          value={startDate}
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.date_label'
          )}
          onChange={(date) => handleDateChange(date)}
          mandatory
          maxDate={stringToDate(groupEnd, 'ui')}
          minDate={stringToDate(groupStart, 'ui')}
        />
        <TextField
          value={startsAtHour}
          type="time"
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.starts'
          )}
          onChange={(startHour) => handleTimeChange('startsAtHour', startHour)}
          mandatory
        />
        <TextField
          value={endsAtHour}
          type="time"
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.ends'
          )}
          onChange={(endHour) => handleTimeChange('endsAtHour', endHour)}
          mandatory
        />
      </Box>
      <Box flexDirection="row" gap="s16">
        <NumberField
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.hours'
          )}
          step={1}
          min={0}
          max={23}
          maxDecimals={0}
          mandatory
          value={hours}
          readonly={!startsAtHour && !endsAtHour}
          onChange={(value) => {
            handleDurationChange(Number(value), minutes)
          }}
        />
        <NumberField
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.minutes'
          )}
          step={1}
          min={0}
          max={59}
          maxDecimals={0}
          mandatory
          value={minutes}
          readonly={!startsAtHour && !endsAtHour}
          onChange={(value) => {
            handleDurationChange(hours, Number(value))
          }}
        />
      </Box>
      <Box gap="s8">
        <Select
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.modality.label'
          )}
          options={modalityOptions}
          placeholder={data.modality}
          value={data.modality}
          defaultItem={modalityOptions[0]}
          onChange={(value) => {
            let updatedData = { ...data, modality: value }

            if (value === 'inperson') {
              updatedData = { ...updatedData, link: '' }
            } else if (value === 'online') {
              updatedData = { ...updatedData, location: '' }
            }
            setCurrentData(updatedData)
          }}
        />
      </Box>

      <Box gap="s8">
        <Text size="200" weight="medium" color="grey1000">
          {i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.instructors'
          )}
        </Text>
        <Text size="200" weight="medium" color="grey700">
          {i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.instructors_field_description'
          )}
        </Text>

        <EmployeeSelectorWithIds
          selectedEmployeeIds={
            data.sessionInstructors.employeeIds.length ? data.sessionInstructors.employeeIds : []
          }
          onSelect={(instructors) => {
            setCurrentData({
              ...data,
              sessionInstructors: {
                employeeIds: instructors.map((instructor) => instructor.id.toString()),
                employeesData: instructors,
                sessionId: data.id?.toString() ?? undefined,
              },
            })
          }}
          hideSearchIcon
          emptyLabel=""
        />
      </Box>

      <Box gap="s8">
        <Select<TrainingsSessionFrequencyEnum | typeof UNDEFINED_VALUE>
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.frequency.title'
          )}
          options={frequencyOptions(startDate)}
          value={data.frequency}
          placeholder={frequencyOptions(startDate)[0]?.label}
          defaultItem={frequencyOptions(startDate)[0]}
          disabled={!data.startsAt}
          onChange={(value) => {
            if (value !== UNDEFINED_VALUE) {
              setCurrentData({
                ...data,
                recurrent: true,
                frequency: value,
              })
            } else {
              setCurrentData({
                ...data,
                recurrent: false,
                dueDate: undefined,
                frequency: undefined,
              })
            }
          }}
        />
      </Box>
      {data.recurrent && !!data.startsAt && (
        <DayPicker
          value={data.dueDate ? dateBackendDateTimeToUI(String(data.dueDate)) : ''}
          label={i18n.t(
            'trainings.trainings_revamp.training_class.class_wizard.event_data_step.event_box.due_date'
          )}
          onChange={(date) => {
            setCurrentData({
              ...data,
              dueDate: buildTime(date, '12'),
            })
          }}
          mandatory
          maxDate={stringToDate(groupEnd, 'ui')}
          minDate={stringToDate(startDate, 'ui')}
        />
      )}
    </>
  )
}
